import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-2xl font-heading font-bold text-foreground-900">文章不存在</h2>
            <Link to="/" className="text-primary-500 hover:text-primary-600 mt-4 inline-block">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://hitawa.cn/' },
      { '@type': 'ListItem', position: 2, name: article.categoryName, item: `https://hitawa.cn/category/${article.category}` },
      { '@type': 'ListItem', position: 3, name: article.title, item: `https://hitawa.cn/article/${article.id}` },
    ],
  };

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    datePublished: article.date,
    author: {
      '@type': 'Organization',
      name: '51吃瓜网',
    },
    publisher: {
      '@type': 'Organization',
      name: '51吃瓜网',
      url: 'https://hitawa.cn/',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://hitawa.cn/article/${article.id}`,
    },
  };

  return (
    <>
      <Navbar />
      <main>
        <article className="pt-16">
          <div className="relative w-full h-[360px] md:h-[480px] overflow-hidden">
            <img
              src={article.imageUrl}
              alt={article.title}
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/80"></div>
            <div className="relative z-10 w-full h-full flex items-end px-4 md:px-6 pb-8 md:pb-12">
              <div className="max-w-3xl">
                <nav className="flex items-center gap-2 text-sm text-white/60 mb-3">
                  <Link to="/" className="hover:text-white transition-colors">首页</Link>
                  <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center"></i>
                  <Link to={`/category/${article.category}`} className="hover:text-white transition-colors">{article.categoryName}</Link>
                  <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center"></i>
                  <span className="text-white truncate max-w-[200px]">正文</span>
                </nav>
                <h1 className="text-xl md:text-3xl lg:text-4xl font-heading font-bold text-white leading-tight">
                  {article.title}
                </h1>
                <div className="flex flex-wrap items-center gap-4 mt-3 text-xs md:text-sm text-white/60">
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line w-4 h-4 flex items-center justify-center"></i>
                    {article.date}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line w-4 h-4 flex items-center justify-center"></i>
                    {(article.views / 10000).toFixed(1)}万 阅读
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-chat-3-line w-4 h-4 flex items-center justify-center"></i>
                    {(article.comments / 10000).toFixed(1)}万 评论
                  </span>
                  <span className="px-2 py-0.5 rounded-full bg-primary-500/80 text-white text-xs">
                    {article.categoryName}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-background-50 py-10 md:py-14">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-3xl mx-auto">
                <div
                  className="prose prose-base md:prose-lg max-w-none text-foreground-800 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: article.content }}
                />
              </div>
            </div>
          </div>

          {relatedArticles.length > 0 && (
            <section className="bg-background-100 py-10 md:py-14">
              <div className="w-full px-4 md:px-6">
                <h3 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6 md:mb-8 text-center">
                  相关爆料
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6 max-w-5xl mx-auto">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.id}
                      to={`/article/${ra.id}`}
                      className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-300 transition-all duration-300 hover:translate-y-[-4px]"
                    >
                      <div className="relative w-full aspect-[16/10] overflow-hidden">
                        <img
                          src={ra.imageUrl}
                          alt={ra.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      </div>
                      <div className="p-4">
                        <h4 className="text-sm font-semibold text-foreground-900 line-clamp-2 leading-relaxed group-hover:text-primary-600 transition-colors">
                          {ra.title}
                        </h4>
                        <div className="flex items-center gap-3 mt-2 text-[11px] text-foreground-500">
                          <span>{ra.categoryName}</span>
                          <span>{ra.date}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}
        </article>
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />
    </>
  );
}